// Results page. Three variants share this file:
//   • one-way                — flat outbound list
//   • domestic round-trip    — two columns (outbound + return)
//   • intl round-trip        — outbound cards w/ nested inbound_options
//
// Highlights in this version:
//   • Cards are React.memo'd — selecting one option no longer re-renders the
//     other 939 cards on the page, which was the source of the click-lag.
//   • Selection-toggle state is held in a small Map<rI,bool>-style structure
//     for the same reason: list re-renders cost less than re-keying by index.
//   • Journey-type tag colors by variant (one-way / domestic-rt / intl-rt).
//   • When the search hold expires we disable interaction and surface a
//     prominent retry banner that re-runs the search with the cached params.
//   • Fare-rules drawer now gets the full FlightOption (no extra fetch for
//     segment data) and the same Continue handler the sticky bar uses.

import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { lookupAirport, warmCacheFromSearch } from "../lib/airports";
import { fmtCabin } from "../lib/labels";
import { loadSearch, saveSearch, type CachedSearch } from "../lib/searchCache";
import { useCountdown } from "../lib/useCountdown";
import {
  DEFAULT_FLIGHT_SOURCE,
  FLIGHT_SOURCES,
  type FlightOption,
  type FlightSource,
  type Itinerary,
  type SearchResponse,
  type SortStat,
  type SortStats,
} from "../lib/types";
import { AirlineLogo } from "../components/AirlineLogo";
import { CountdownPill } from "../components/HoldTimer";
import { FareRulesDrawer } from "../components/FareRulesDrawer";
import { ProviderSwitch } from "../components/ProviderSwitch";

type SortMode = "cheapest" | "fastest" | "earliest";

interface Filters {
  stops: Set<"direct" | "1" | "2+">;
  refundable: boolean | null;
  airlines: Set<string>;
}

// Fallback result lifetime, used when the provider doesn't tell us its own.
// TravClan runs a flat 15-minute trace_id window; Nuitee returns the real
// offer expiry on the search response (`remaining_seconds`), which is usually
// shorter, so we honour that whenever it's present.
const RESULT_TTL_MS = 15 * 60_000;

export function Results() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const cached = useMemo(() => loadSearch(), []);
  useEffect(() => {
    if (cached) warmCacheFromSearch(cached.response);
  }, [cached]);

  // Re-search mutation used by the expired banner — same params as the
  // original search; on success we update the cache + reset selection.
  const reSearch = useMutation({
    mutationFn: async (form: CachedSearch["form"]) => {
      const payload = {
        direct_flight: false,
        adult_count: form.adult,
        child_count: form.child,
        infant_count: form.infant,
        flight_cabin_class: form.cabin_class,
        journey_type: form.journey_type === "one_way" ? 1 : 2,
        preferred_departure_time: `${form.outbound_date}T00:00:00`,
        ...(form.journey_type === "round_trip" && form.inbound_date
          ? { preferred_return_departure_time: `${form.inbound_date}T00:00:00` }
          : {}),
        origin: form.origin,
        destination: form.destination,
        currency: form.currency,
        // Whatever provider the caller asked for. The expired-results banner
        // passes the form back unchanged so a retry stays on the provider the
        // partner was already looking at; the source switch passes a form with
        // a different `source`, which is the one case where landing on another
        // upstream is the point.
        source: form.source,
      };
      return api.post<SearchResponse>("/v1/flights/search/", payload);
    },
    onSuccess: (resp, form) => {
      saveSearch({ form, response: resp, fetched_at: Date.now() });
      // Hard reload to reset all per-mount state without rebuilding the
      // entire component tree by hand.
      window.location.reload();
    },
  });

  if (!cached) {
    return (
      <div className="container" style={{ padding: "60px 0" }}>
        <div className="app-error">
          No search results in this session. <a href="/flights/">Start a new search</a>.
        </div>
      </div>
    );
  }

  const resp = cached.response;
  // Which round-trip layout the provider returned. TravClan couples the two
  // directions onto one result_index for international routes only; Nuitee
  // couples every round trip, domestic included — so read the provider's own
  // answer and only fall back to the is_domestic rule for responses cached
  // before the field existed.
  const rtStyle = roundTripStyle(resp);
  const isCoupledRT = rtStyle === "coupled";
  const isSplitRT = rtStyle === "independent";

  const allAirlines = useMemo(() => collectAirlines(resp), [resp]);

  const [filters, setFilters] = useState<Filters>({
    stops: new Set(),
    refundable: null,
    airlines: new Set(),
  });
  const [sort, setSort] = useState<SortMode>("cheapest");

  const filteredOutbound = useMemo(() => applyFilters(resp.outbound, filters), [resp.outbound, filters]);
  const filteredInbound  = useMemo(() => applyFilters(resp.inbound, filters),  [resp.inbound, filters]);
  const sortedOutbound = useMemo(() => sortOptions(filteredOutbound, sort), [filteredOutbound, sort]);
  const sortedInbound  = useMemo(() => sortOptions(filteredInbound, sort),  [filteredInbound, sort]);

  // Selection state keyed by RI (not index) so the memoized cards can compare
  // their own ID against a single boolean. Avoids re-keying when filters /
  // sort change order.
  const [pickedOutboundRI, setPickedOutboundRI] = useState<string | null>(null);
  const [pickedInboundRI, setPickedInboundRI] = useState<string | null>(null);
  const [expandedIntl, setExpandedIntl] = useState<number | null>(isCoupledRT ? 0 : null);
  const [intlPickedRI, setIntlPickedRI] = useState<string | null>(null);

  const [rules, setRules] = useState<{ options: FlightOption[]; rI: string } | null>(null);

  const createBooking = useMutation({
    mutationFn: (result_indices: string[]) =>
      api.post<Itinerary>("/v1/flights/booking/", {
        trace_id: resp.trace_id,
        result_indices,
        // Pin explicitly. The backend can also recover the provider from the
        // trace, but naming it here means a create never depends on a cache
        // entry still being alive.
        source: cached.form.source ?? resp.provider,
      }),
    onSuccess: (b) => {
      // Seed React Query's cache with the freshly-created booking so the
      // Passengers page renders instantly — no second roundtrip flash.
      qc.setQueryData(["booking", b.booking_id], b);
      nav(`/booking/${b.booking_id}/passengers`);
    },
  });

  // Resolve picked options from RI for the sticky bar + drawer.
  const pickedOutbound = useMemo(() => findByRI(sortedOutbound, pickedOutboundRI), [sortedOutbound, pickedOutboundRI]);
  const pickedInbound  = useMemo(() => findByRI(sortedInbound, pickedInboundRI),   [sortedInbound, pickedInboundRI]);
  const pickedIntlPair = useMemo(() => {
    if (!intlPickedRI) return null;
    for (const ob of sortedOutbound) {
      for (const ib of (ob.inbound_options ?? [])) {
        if (ib.result_index === intlPickedRI) return { ob, ib };
      }
    }
    return null;
  }, [sortedOutbound, intlPickedRI]);

  const onPickOutbound = useCallback((rI: string) => {
    setPickedOutboundRI((cur) => (cur === rI ? null : rI));
  }, []);
  const onPickInbound = useCallback((rI: string) => {
    setPickedInboundRI((cur) => (cur === rI ? null : rI));
  }, []);
  const onPickIntl = useCallback((rI: string) => {
    setIntlPickedRI((cur) => (cur === rI ? null : rI));
  }, []);

  function onContinue() {
    if (resp.journey_type === "one_way" && pickedOutbound) {
      createBooking.mutate([pickedOutbound.result_index]);
    } else if (isSplitRT && pickedOutbound && pickedInbound) {
      createBooking.mutate([pickedOutbound.result_index, pickedInbound.result_index]);
    } else if (isCoupledRT && pickedIntlPair) {
      createBooking.mutate([pickedIntlPair.ib.result_index]);
    }
  }

  const kairaTip = useMemo(() => computeKairaTip(resp), [resp]);
  const sticky = computeSticky(resp, pickedOutbound, pickedInbound, pickedIntlPair);

  const ttlMs = resp.remaining_seconds != null
    ? resp.remaining_seconds * 1000
    : RESULT_TTL_MS;
  const expiresAtMs = cached.fetched_at + ttlMs;
  const remainingSecs = useCountdown(expiresAtMs);
  const expired = remainingSecs != null && remainingSecs <= 0;

  const fromInfo = lookupAirport(cached.form.origin);
  const toInfo = lookupAirport(cached.form.destination);
  const journeyVariant = !cached.form.inbound_date
    ? "one-way"
    : resp.is_domestic ? "domestic-rt" : "intl-rt";
  const journeyLabel = journeyVariant === "one-way" ? "One-way"
    : journeyVariant === "domestic-rt" ? "Domestic round-trip"
    : "International round-trip";

  // What the response says served it wins over what we asked for: a partner who
  // switched provider needs the control to reflect the results on screen, not
  // the request that produced them.
  const activeSource = (resp.provider || cached.form.source || DEFAULT_FLIGHT_SOURCE) as FlightSource;

  // Re-run this exact search against another provider. Fares, fare rules and
  // availability are all provider-specific, so this is a new search rather than
  // a filter — hence the full reload once it lands.
  //
  // `form` is lifted out because a hoisted function declaration loses the
  // `if (!cached) return` narrowing above — TS has to assume it could be called
  // before the guard ran.
  const form = cached.form;
  function switchSource(next: FlightSource) {
    reSearch.mutate({ ...form, source: next });
  }

  function openRulesForSticky() {
    if (!sticky) return;
    setRules({ options: sticky.options, rI: sticky.rulesResultIndex });
  }

  return (
    <>
      <section className="results-topbar">
        <div className="container topbar-inner">
          <div className="summary">
            <div className="pair"><b>{fromInfo.city}</b><span>{fromInfo.code}</span></div>
            {cached.form.inbound_date
              ? <span className="swap-mini">⇄</span>
              : <div className="arrow-line" />}
            <div className="pair"><b>{toInfo.city}</b><span>{toInfo.code}</span></div>
            <span className={`tag journey ${journeyVariant}`}>{journeyLabel}</span>
            <div className="when-pax">
              <b>{fmtPrettyDate(cached.form.outbound_date)}</b>
              {cached.form.inbound_date && <> → <b>{fmtPrettyDate(cached.form.inbound_date)}</b></>}
              {" "}&nbsp;·&nbsp; {paxLabel(cached.form)} · {fmtCabinFromCode(cached.form.cabin_class)}
            </div>
          </div>
          <ProviderSwitch
            sources={FLIGHT_SOURCES}
            value={activeSource}
            onChange={switchSource}
            pending={reSearch.isPending ? (reSearch.variables?.source ?? null) : null}
          />
          <CountdownPill targetMs={expiresAtMs} prefix="Results valid" expiredText="Results expired" />
          <button type="button" className="modify" onClick={() => nav("/")}>Modify search</button>
        </div>
      </section>

      <div className={`container${expired ? " results-expired" : ""}`}>
        <div className="results-wrap">
          <FilterPanel
            filters={filters}
            onChange={setFilters}
            airlines={allAirlines}
            outboundCounts={countByFilter(resp.outbound)}
            inboundCounts={isSplitRT ? countByFilter(resp.inbound) : null}
          />

          <div>
            {expired && (
              <div className="expired-banner">
                <svg viewBox="0 0 24 24" width={22} height={22} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                <div className="body">
                  <b>Your results expired</b>
                  <span>Prices and seat availability may have changed. Re-run the same search to get fresh results.</span>
                </div>
                <button
                  type="button"
                  className={`btn btn-yellow btn-pill${reSearch.isPending ? " loading" : ""}`}
                  disabled={reSearch.isPending}
                  onClick={() => reSearch.mutate(cached.form)}
                >
                  Retry search
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9M3 3v6h6"/></svg>
                </button>
              </div>
            )}

            {kairaTip && !expired && (
              <div className="kaira-tip">
                <div className="k-avatar-mini" />
                <div className="copy" dangerouslySetInnerHTML={{ __html: kairaTip }} />
              </div>
            )}

            <SortRow sort={sort} onChange={setSort} stats={resp.sort_stats} currency={resp.currency} count={
              isCoupledRT
                ? `${filteredOutbound.length} outbound · ${countPairings(filteredOutbound)} pairings`
                : isSplitRT
                ? `${filteredOutbound.length} outbound · ${filteredInbound.length} return`
                : `${filteredOutbound.length} options`
            }/>

            {resp.journey_type === "one_way" && (
              <OneWayList
                options={sortedOutbound}
                pickedRI={pickedOutboundRI}
                currency={resp.currency}
                onPick={onPickOutbound}
              />
            )}

            {isSplitRT && (
              <DomesticRT
                outbound={sortedOutbound}
                inbound={sortedInbound}
                currency={resp.currency}
                pickedOutboundRI={pickedOutboundRI}
                pickedInboundRI={pickedInboundRI}
                onPickOutbound={onPickOutbound}
                onPickInbound={onPickInbound}
              />
            )}

            {isCoupledRT && (
              <IntlRT
                outbound={sortedOutbound}
                currency={resp.currency}
                expanded={expandedIntl}
                setExpanded={setExpandedIntl}
                pickedRI={intlPickedRI}
                onPick={onPickIntl}
              />
            )}

            {createBooking.error && (
              <div className="app-error" style={{ margin: "16px 0" }}>
                {(createBooking.error as Error).message}
              </div>
            )}

            {sticky && !expired && (
              <div className="sticky-bottom">
                <div className="sticky-summary">
                  <div className="picked">
                    <b>{sticky.heading}</b>
                    <span>{sticky.detail}</span>
                  </div>
                  <div className="total">
                    <span className="amt">{fmtMoney(sticky.amount, resp.currency)}</span>
                    <span className="sub">taxes included</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <button type="button" className="rules-btn" onClick={openRulesForSticky}>View fare rules</button>
                    <button
                      type="button"
                      className={`btn btn-yellow btn-pill${createBooking.isPending ? " loading" : ""}`}
                      disabled={createBooking.isPending}
                      onClick={onContinue}
                    >
                      Continue
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {rules && (
        <FareRulesDrawer
          trace_id={resp.trace_id}
          options={rules.options}
          result_index={rules.rI}
          currency={resp.currency}
          onClose={() => setRules(null)}
          onContinue={() => { setRules(null); onContinue(); }}
          continueDisabled={!sticky}
          continueLoading={createBooking.isPending}
        />
      )}
    </>
  );
}

// ─── filter panel ───────────────────────────────────────────────────────────

function FilterPanel(props: {
  filters: Filters;
  onChange: (f: Filters) => void;
  airlines: Array<{ code: string; name: string }>;
  outboundCounts: ReturnType<typeof countByFilter>;
  inboundCounts: ReturnType<typeof countByFilter> | null;
}) {
  const { filters, onChange } = props;
  const stopsCount = (k: "direct" | "1" | "2+") => props.outboundCounts.stops[k] + (props.inboundCounts?.stops[k] ?? 0);
  const refundableCount = (yes: boolean) => (yes ? props.outboundCounts.refundable : props.outboundCounts.nonRefundable) + (props.inboundCounts ? (yes ? props.inboundCounts.refundable : props.inboundCounts.nonRefundable) : 0);
  const airlineCount = (code: string) => (props.outboundCounts.airlines[code] || 0) + (props.inboundCounts?.airlines[code] || 0);

  function toggleStop(k: "direct" | "1" | "2+") {
    const next = new Set(filters.stops);
    next.has(k) ? next.delete(k) : next.add(k);
    onChange({ ...filters, stops: next });
  }
  function toggleAirline(code: string) {
    const next = new Set(filters.airlines);
    next.has(code) ? next.delete(code) : next.add(code);
    onChange({ ...filters, airlines: next });
  }
  function setRefundable(v: boolean | null) {
    onChange({ ...filters, refundable: v });
  }
  function clearAll() {
    onChange({ stops: new Set(), refundable: null, airlines: new Set() });
  }

  return (
    <aside className="filter-panel">
      <div className="filter-panel-head">
        <h3>Filters</h3>
        <button type="button" className="clear" onClick={clearAll}>Clear all</button>
      </div>

      <div className="filter-group">
        <h4>Stops</h4>
        {([["direct", "Non-stop"], ["1", "1 stop"], ["2+", "2+ stops"]] as Array<["direct" | "1" | "2+", string]>).map(([k, label]) => (
          <label key={k} className="filter-row">
            <span className="left">
              <input type="checkbox" checked={filters.stops.has(k)} onChange={() => toggleStop(k)} /> {label}
            </span>
            <span className="count">{stopsCount(k)}</span>
          </label>
        ))}
      </div>

      <div className="filter-group">
        <h4>Fare type</h4>
        <label className="filter-row">
          <span className="left">
            <input type="checkbox" checked={filters.refundable === true} onChange={(e) => setRefundable(e.target.checked ? true : null)} /> Refundable
          </span>
          <span className="count">{refundableCount(true)}</span>
        </label>
        <label className="filter-row">
          <span className="left">
            <input type="checkbox" checked={filters.refundable === false} onChange={(e) => setRefundable(e.target.checked ? false : null)} /> Non-refundable
          </span>
          <span className="count">{refundableCount(false)}</span>
        </label>
      </div>

      <div className="filter-group">
        <h4>Airline</h4>
        {props.airlines.map((a) => (
          <label key={a.code} className="filter-row">
            <span className="left">
              <input type="checkbox" checked={filters.airlines.has(a.code)} onChange={() => toggleAirline(a.code)} /> {a.name || a.code}
            </span>
            <span className="count">{airlineCount(a.code)}</span>
          </label>
        ))}
      </div>
    </aside>
  );
}

function applyFilters(options: FlightOption[], f: Filters): FlightOption[] {
  return options.filter((o) => {
    if (f.stops.size > 0) {
      const bucket = o.stop_count === 0 ? "direct" : o.stop_count === 1 ? "1" : "2+";
      if (!f.stops.has(bucket)) return false;
    }
    if (f.refundable === true && !o.is_refundable) return false;
    if (f.refundable === false && o.is_refundable) return false;
    if (f.airlines.size > 0) {
      const ok = o.segments.some((s) => f.airlines.has(s.airline.code));
      if (!ok) return false;
    }
    return true;
  });
}

function countByFilter(options: FlightOption[]) {
  const stops = { direct: 0, "1": 0, "2+": 0 } as Record<"direct" | "1" | "2+", number>;
  const airlines: Record<string, number> = {};
  let refundable = 0;
  let nonRefundable = 0;
  for (const o of options) {
    const k = o.stop_count === 0 ? "direct" : o.stop_count === 1 ? "1" : "2+";
    stops[k]++;
    if (o.is_refundable) refundable++; else nonRefundable++;
    for (const s of o.segments) {
      const c = s.airline.code;
      if (c) airlines[c] = (airlines[c] || 0) + 1;
    }
  }
  return { stops, refundable, nonRefundable, airlines };
}

function collectAirlines(resp: SearchResponse): Array<{ code: string; name: string }> {
  const map = new Map<string, string>();
  const add = (opts: FlightOption[]) => {
    for (const o of opts) {
      for (const s of o.segments) {
        if (s.airline.code) map.set(s.airline.code, s.airline.name || s.airline.code);
      }
      for (const io of (o.inbound_options || [])) {
        for (const s of io.segments) {
          if (s.airline.code) map.set(s.airline.code, s.airline.name || s.airline.code);
        }
      }
    }
  };
  add(resp.outbound);
  add(resp.inbound);
  return Array.from(map, ([code, name]) => ({ code, name })).sort((a, b) => a.name.localeCompare(b.name));
}

// ─── sort row ──────────────────────────────────────────────────────────────

function SortRow({ sort, onChange, stats, currency, count }: {
  sort: SortMode; onChange: (s: SortMode) => void;
  stats: SortStats | undefined;
  currency: string;
  count: string;
}) {
  const tabs: Array<{ key: SortMode; label: string }> = [
    { key: "cheapest", label: "Cheapest" },
    { key: "fastest",  label: "Fastest" },
    { key: "earliest", label: "Earliest" },
  ];
  return (
    <div className="sort-row">
      {tabs.map((t) => {
        const s = stats?.[t.key];
        return (
          <button
            key={t.key}
            type="button"
            className={`sort-tab${sort === t.key ? " on" : ""}`}
            onClick={() => onChange(t.key)}
          >
            <span className="label">{t.label}</span>
            <span className="value">{primaryValue(t.key, s, currency)}</span>
            <span className="sub">{subValue(t.key, s, currency)}</span>
          </button>
        );
      })}
      <span className="results-count">{count}</span>
    </div>
  );
}
function primaryValue(mode: SortMode, s: SortStat | undefined, currency: string): string {
  if (!s) return "—";
  if (mode === "cheapest") return fmtMoney(s.fare, currency);
  if (mode === "fastest")  return fmtDuration(s.duration_minutes);
  return fmtTime(s.departure_time);
}
function subValue(mode: SortMode, s: SortStat | undefined, currency: string): string {
  if (!s) return "";
  if (mode === "cheapest") return s.airlines.length > 1 ? s.airlines.join(" + ") : fmtDuration(s.duration_minutes);
  if (mode === "fastest")  return fmtMoney(s.fare, currency);
  return fmtMoney(s.fare, currency);
}

// ─── lists ──────────────────────────────────────────────────────────────────

function OneWayList(props: {
  options: FlightOption[]; pickedRI: string | null; currency: string;
  onPick: (rI: string) => void;
}) {
  if (props.options.length === 0) return <NoResults />;
  return (
    <>
      {props.options.map((o) => (
        <FlightArticle
          key={o.result_index}
          option={o}
          currency={props.currency}
          selected={props.pickedRI === o.result_index}
          onPick={props.onPick}
        />
      ))}
    </>
  );
}

// Memoized so changing selection in another card doesn't re-render this one.
const FlightArticle = memo(function FlightArticle(props: {
  option: FlightOption; currency: string; selected: boolean; onPick: (rI: string) => void;
}) {
  const o = props.option;
  const first = o.segments[0];
  const last = o.segments[o.segments.length - 1];
  const total = o.segments.reduce((s, x) => s + x.duration_minutes + x.ground_time_minutes, 0);
  const nextDay = isNextDay(first.origin.departure_time, last.destination.arrival_time);
  const handle = useCallback(() => props.onPick(o.result_index), [props.onPick, o.result_index]);
  return (
    <article
      className={`flight-card${props.selected ? " selected" : ""}${o.kaira_picked ? " has-kaira" : ""}`}
      onClick={handle}
      style={{ cursor: "pointer" }}
    >
      {o.kaira_picked && <KairaCorner />}
      <div>
        <div className="flight-row">
          <div className="airline-cluster">
            <AirlineLogo code={first.airline.code} name={first.airline.name} />
            <div className="airline-name">{first.airline.code}</div>
            <div className="airline-num">{first.airline.flight_number}</div>
          </div>
          <div>
            <div className="flight-time">{fmtTime(first.origin.departure_time)}</div>
            <div className="flight-airport"><b>{first.origin.airport_code}</b>{first.origin.terminal ? ` · ${first.origin.terminal}` : ""}</div>
          </div>
          <div className="flight-mid">
            <div className="flight-duration">{fmtDuration(total)}</div>
            <div className="flight-line" />
            <div className={`flight-stops${o.stop_count === 0 ? " direct" : ""}`}>
              {o.stop_count === 0 ? "Non-stop" : `${o.stop_count} stop · ${o.segments[0].destination.airport_code}`}
            </div>
          </div>
          <div>
            <div className="flight-time">{fmtTime(last.destination.arrival_time)} {nextDay && <span className="next-day">+1</span>}</div>
            <div className="flight-airport"><b>{last.destination.airport_code}</b>{last.destination.terminal ? ` · ${last.destination.terminal}` : ""}</div>
          </div>
        </div>
        <div className="flight-meta">
          <span className={`tag ${o.is_refundable ? "refundable" : "non-refundable"}`}>{o.is_refundable ? "Refundable" : "Non-refundable"}</span>
          {first.baggage_allowance && <span className="tag">{first.baggage_allowance}{first.cabin_baggage_allowance ? ` + ${first.cabin_baggage_allowance} cabin` : ""}</span>}
          <span className="tag">{fmtCabin(first.cabin_class)}</span>
          {first.seats_available != null && first.seats_available <= 9 && (
            <span className="tag rose">{first.seats_available} seats left</span>
          )}
        </div>
      </div>
      <div className="flight-right">
        <div className="fare-amount">{fmtMoney(o.pricing.final_fare, o.pricing.currency || props.currency)}</div>
        <div className="fare-sub">total · taxes included</div>
        <button
          type="button"
          className={`btn btn-pill ${props.selected ? "btn-yellow" : "btn-primary"}`}
          onClick={(e) => { e.stopPropagation(); handle(); }}
        >
          {props.selected ? "Selected ✓" : "Select"}
        </button>
      </div>
    </article>
  );
});

function DomesticRT(props: {
  outbound: FlightOption[]; inbound: FlightOption[];
  currency: string;
  pickedOutboundRI: string | null; pickedInboundRI: string | null;
  onPickOutbound: (rI: string) => void; onPickInbound: (rI: string) => void;
}) {
  const obFirst = props.outbound[0];
  const ibFirst = props.inbound[0];
  return (
    <div className="rt-split">
      <DomColumn
        title="Outbound"
        route={obFirst ? `${lookupAirport(obFirst.segments[0].origin.airport_code).city} → ${lookupAirport(obFirst.segments[obFirst.segments.length - 1].destination.airport_code).city}` : ""}
        options={props.outbound}
        pickedRI={props.pickedOutboundRI}
        currency={props.currency}
        onPick={props.onPickOutbound}
      />
      <DomColumn
        title="Return"
        route={ibFirst ? `${lookupAirport(ibFirst.segments[0].origin.airport_code).city} → ${lookupAirport(ibFirst.segments[ibFirst.segments.length - 1].destination.airport_code).city}` : ""}
        options={props.inbound}
        pickedRI={props.pickedInboundRI}
        currency={props.currency}
        onPick={props.onPickInbound}
      />
    </div>
  );
}

function DomColumn(props: {
  title: string; route: string;
  options: FlightOption[]; pickedRI: string | null; currency: string;
  onPick: (rI: string) => void;
}) {
  return (
    <div className="rt-column">
      <h4>{props.title} <span className="route">{props.route}</span></h4>
      {props.options.length === 0 ? <NoResults /> : props.options.map((o) => (
        <CompactFlight
          key={o.result_index}
          option={o}
          currency={props.currency}
          selected={props.pickedRI === o.result_index}
          onPick={props.onPick}
        />
      ))}
    </div>
  );
}

// Memoized for the same reason as FlightArticle.
const CompactFlight = memo(function CompactFlight(props: {
  option: FlightOption; currency: string; selected: boolean; onPick: (rI: string) => void;
  combined?: boolean;
}) {
  const o = props.option;
  const first = o.segments[0];
  const last = o.segments[o.segments.length - 1];
  const total = o.segments.reduce((s, x) => s + x.duration_minutes + x.ground_time_minutes, 0);
  const nextDay = isNextDay(first.origin.departure_time, last.destination.arrival_time);
  const handle = useCallback(() => props.onPick(o.result_index), [props.onPick, o.result_index]);
  return (
    <article
      className={`flight-card compact${props.selected ? " selected" : ""}${o.kaira_picked ? " has-kaira" : ""}`}
      onClick={handle}
      style={{ cursor: "pointer" }}
    >
      {o.kaira_picked && <KairaCorner />}
      <div className="flight-row">
        <div className="airline-cluster">
          <AirlineLogo code={first.airline.code} name={first.airline.name} />
          <div className="airline-name">{first.airline.code}</div>
          <div className="airline-num">{first.airline.flight_number}</div>
        </div>
        <div>
          <div className="flight-time">{fmtTime(first.origin.departure_time)}</div>
          <div className="flight-airport">{first.origin.airport_code}{first.origin.terminal ? ` · T${first.origin.terminal}` : ""}</div>
        </div>
        <div className="flight-mid">
          <div className="flight-duration">{fmtDuration(total)}</div>
          <div className="flight-line" />
          <div className={`flight-stops${o.stop_count === 0 ? " direct" : ""}`}>{o.stop_count === 0 ? "Direct" : `${o.stop_count} stop`}</div>
        </div>
        <div>
          <div className="flight-time">{fmtTime(last.destination.arrival_time)} {nextDay && <span className="next-day">+1</span>}</div>
          <div className="flight-airport">{last.destination.airport_code}{last.destination.terminal ? ` · T${last.destination.terminal}` : ""}</div>
        </div>
      </div>
      <div className="flight-right compact-right">
        <div className="compact-tags">
          <span className={`tag ${o.is_refundable ? "refundable" : "non-refundable"}`}>{o.is_refundable ? "Refundable" : "Non-refundable"}</span>
          {first.baggage_allowance && <span className="tag">{first.baggage_allowance}</span>}
          {first.seats_available != null && first.seats_available <= 9 && (
            <span className="tag rose">{first.seats_available} left</span>
          )}
        </div>
        <div className="compact-amt-pick">
          <div style={{ textAlign: "right" }}>
            <div className="fare-amount">{fmtMoney(o.pricing.final_fare, o.pricing.currency || props.currency)}</div>
            <div className="fare-sub">{props.combined ? "combined · total" : "total · taxes incl."}</div>
          </div>
          <button
            type="button"
            className={`btn btn-pill btn-sm ${props.selected ? "btn-yellow" : "btn-primary"}`}
            onClick={(e) => { e.stopPropagation(); handle(); }}
          >
            {props.selected ? "Selected ✓" : "Select"}
          </button>
        </div>
      </div>
    </article>
  );
});

function IntlRT(props: {
  outbound: FlightOption[]; currency: string;
  expanded: number | null; setExpanded: (i: number | null) => void;
  pickedRI: string | null;
  onPick: (rI: string) => void;
}) {
  if (props.outbound.length === 0) return <NoResults />;
  return (
    <>
      {props.outbound.map((ob, obIdx) => {
        const isOpen = props.expanded === obIdx;
        const first = ob.segments[0];
        const last = ob.segments[ob.segments.length - 1];
        const total = ob.segments.reduce((s, x) => s + x.duration_minutes + x.ground_time_minutes, 0);
        const inbounds = ob.inbound_options ?? [];
        const cheapest = inbounds.length ? Math.min(...inbounds.map((i) => i.pricing.final_fare)) : ob.pricing.final_fare;
        return (
          <article key={ob.result_index} className={`intl-card${isOpen ? " open" : ""}${ob.kaira_picked ? " has-kaira" : ""}`}>
            {ob.kaira_picked && <KairaCorner />}
            <div className="intl-head" onClick={() => props.setExpanded(isOpen ? null : obIdx)}>
              <div className="flight-row">
                <div className="airline-cluster">
                  <AirlineLogo code={first.airline.code} name={first.airline.name} />
                  <div className="airline-name">{first.airline.code}</div>
                  <div className="airline-num">{first.airline.flight_number}</div>
                </div>
                <div>
                  <div className="flight-time">{fmtTime(first.origin.departure_time)}</div>
                  <div className="flight-airport"><b>{first.origin.airport_code}</b>{first.origin.terminal ? ` · ${first.origin.terminal}` : ""}</div>
                </div>
                <div className="flight-mid">
                  <div className="flight-duration">{fmtDuration(total)}</div>
                  <div className="flight-line" />
                  <div className={`flight-stops${ob.stop_count === 0 ? " direct" : ""}`}>{ob.stop_count === 0 ? "Non-stop" : `${ob.stop_count} stop · ${ob.segments[0].destination.airport_code}`}</div>
                </div>
                <div>
                  <div className="flight-time">{fmtTime(last.destination.arrival_time)}</div>
                  <div className="flight-airport"><b>{last.destination.airport_code}</b>{last.destination.terminal ? ` · ${last.destination.terminal}` : ""}</div>
                </div>
              </div>
              <div className="intl-starting">
                <div className="lbl">Starting from</div>
                <div className="amt">{fmtMoney(cheapest, ob.pricing.currency || props.currency)}</div>
                <div className="count">
                  {inbounds.length} return option{inbounds.length !== 1 ? "s" : ""}
                  <span className="intl-chevron">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
                  </span>
                </div>
              </div>
            </div>

            {isOpen && (
              <div className="intl-returns">
                <div className="intl-returns-label">
                  <span><b>Return options</b> · {inbounds[0]?.segments[0].origin.airport_code ?? ""} → {inbounds[0]?.segments[inbounds[0].segments.length - 1]?.destination.airport_code ?? ""}</span>
                  <span>{inbounds.length} pairings — sorted by combined fare</span>
                </div>
                {[...inbounds].sort((a, b) => a.pricing.final_fare - b.pricing.final_fare).map((ib) => (
                  <CompactFlight
                    key={ib.result_index}
                    option={ib}
                    currency={props.currency}
                    selected={props.pickedRI === ib.result_index}
                    onPick={props.onPick}
                    combined
                  />
                ))}
              </div>
            )}
          </article>
        );
      })}
    </>
  );
}

function NoResults() {
  return (
    <div className="flight-card" style={{ display: "block", textAlign: "center", color: "var(--ink-3)" }}>
      No flights match the current filters.
    </div>
  );
}

// Top-right corner overlay on any card whose option is kaira-picked. Pure
// presentational; positioned via .kaira-corner in pages.css.
function KairaCorner() {
  return (
    <span className="kaira-corner">
      <span className="k">K</span> picked
    </span>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

/** Round-trip layout, from the provider's own answer. Falls back to the
 *  TravClan-specific is_domestic rule only for responses cached before
 *  `round_trip_style` existed. */
function roundTripStyle(resp: SearchResponse): "coupled" | "independent" | null {
  if (resp.journey_type !== "round_trip") return null;
  return resp.round_trip_style ?? (resp.is_domestic ? "independent" : "coupled");
}

function findByRI(list: FlightOption[], rI: string | null): FlightOption | null {
  if (!rI) return null;
  return list.find((o) => o.result_index === rI) || null;
}

function computeKairaTip(resp: SearchResponse): string | null {
  const style = roundTripStyle(resp);
  if (style === "independent") {
    return `<b>These round-trips are sold as independent legs.</b> Pick any outbound, pair it with any return — different airlines work fine.`;
  }
  if (style === "coupled") {
    return `<b>These round-trips are sold as combinations.</b> Each outbound has its own list of pairable returns — tap an option to expand and pick the pair.`;
  }
  const stats = resp.sort_stats;
  if (!stats) return null;
  const { cheapest, fastest, earliest } = stats;
  const sameAirline = cheapest.airlines.length === 1 && fastest.airlines[0] === cheapest.airlines[0];
  const diff = fastest.fare - cheapest.fare;
  if (sameAirline && diff <= 500) {
    return `<b>Cheapest and fastest are both ${cheapest.airlines[0]}.</b> ${cheapest.airlines[0]} at ${fmtTime(cheapest.departure_time)} for ${fmtMoney(cheapest.fare, resp.currency)} is the best mix — and the earliest leaves at ${fmtTime(earliest.departure_time)}.`;
  }
  return `<b>Cheapest is ${cheapest.airlines.join(" + ")} at ${fmtMoney(cheapest.fare, resp.currency)}.</b> Fastest takes ${fmtDuration(fastest.duration_minutes)} for ${fmtMoney(fastest.fare, resp.currency)}.`;
}

interface StickyData {
  heading: string;
  detail: string;
  amount: number;
  rulesResultIndex: string;
  /** Full FlightOptions the drawer should render. */
  options: FlightOption[];
}

function computeSticky(
  resp: SearchResponse,
  pickedOutbound: FlightOption | null,
  pickedInbound: FlightOption | null,
  pickedIntl: { ob: FlightOption; ib: FlightOption } | null,
): StickyData | null {
  const style = roundTripStyle(resp);
  const isSplitRT = style === "independent";
  const isCoupledRT = style === "coupled";

  if (resp.journey_type === "one_way" && pickedOutbound) {
    return {
      heading: "Your pick",
      detail: legSummary(pickedOutbound),
      amount: pickedOutbound.pricing.final_fare,
      rulesResultIndex: pickedOutbound.result_index,
      options: [pickedOutbound],
    };
  }
  if (isSplitRT && pickedOutbound && pickedInbound) {
    return {
      heading: "Your pick — outbound + return",
      detail: `${legSummary(pickedOutbound)} · ${legSummary(pickedInbound)}`,
      amount: pickedOutbound.pricing.final_fare + pickedInbound.pricing.final_fare,
      rulesResultIndex: pickedOutbound.result_index,
      options: [pickedOutbound, pickedInbound],
    };
  }
  if (isCoupledRT && pickedIntl) {
    return {
      heading: "Your pick — round-trip combo",
      detail: `${legSummary(pickedIntl.ob)} · ${legSummary(pickedIntl.ib)}`,
      amount: pickedIntl.ib.pricing.final_fare,
      rulesResultIndex: pickedIntl.ib.result_index,
      options: [pickedIntl.ob, pickedIntl.ib],
    };
  }
  return null;
}

function sortOptions(list: FlightOption[], mode: SortMode): FlightOption[] {
  const out = [...list];
  out.sort((a, b) => {
    // Keep kaira picks pinned to the top — they're already first in the
    // server response; preserve that order across sort changes.
    const pa = a.kaira_picked ? 0 : 1;
    const pb = b.kaira_picked ? 0 : 1;
    if (pa !== pb) return pa - pb;
    if (mode === "cheapest") return a.pricing.final_fare - b.pricing.final_fare;
    if (mode === "fastest") return legDur(a) - legDur(b);
    return depTime(a) - depTime(b);
  });
  return out;
}
function legDur(o: FlightOption): number {
  return o.segments.reduce((s, x) => s + x.duration_minutes + x.ground_time_minutes, 0);
}
function depTime(o: FlightOption): number {
  return new Date(o.segments[0].origin.departure_time || 0).getTime();
}
function countPairings(outbound: FlightOption[]): number {
  return outbound.reduce((s, o) => s + (o.inbound_options?.length ?? 0), 0);
}
function isNextDay(depISO?: string, arrISO?: string): boolean {
  if (!depISO || !arrISO) return false;
  const d = new Date(depISO);
  const a = new Date(arrISO);
  if (Number.isNaN(d.getTime()) || Number.isNaN(a.getTime())) return false;
  return a.getUTCDate() !== d.getUTCDate() || a.getUTCMonth() !== d.getUTCMonth();
}
function legSummary(o: FlightOption): string {
  const first = o.segments[0];
  const last = o.segments[o.segments.length - 1];
  return `${first.airline.code} ${first.airline.flight_number} · ${fmtTime(first.origin.departure_time)} → ${fmtTime(last.destination.arrival_time)}`;
}
function fmtTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtDuration(mins: number): string {
  if (!Number.isFinite(mins)) return "—";
  const h = Math.floor(mins / 60); const m = mins % 60;
  return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${String(m).padStart(2, "0")}m`;
}
function fmtMoney(n: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${currency} ${Math.round(n).toLocaleString("en-IN")}`;
  }
}
function fmtPrettyDate(ymd: string): string {
  const d = new Date(ymd + "T00:00:00");
  return Number.isNaN(d.getTime()) ? ymd : d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}
function paxLabel(form: { adult: number; child: number; infant: number }): string {
  const parts: string[] = [];
  if (form.adult) parts.push(`${form.adult} adult${form.adult > 1 ? "s" : ""}`);
  if (form.child) parts.push(`${form.child} child${form.child > 1 ? "ren" : ""}`);
  if (form.infant) parts.push(`${form.infant} infant${form.infant > 1 ? "s" : ""}`);
  return parts.join(" · ");
}
// Index = the API's `flight_cabin_class` code (flights/schemas.py CABIN_CLASS).
function fmtCabinFromCode(c: number): string {
  return ["", "Any cabin", "Economy", "Premium economy", "Business",
          "Premium business", "First"][c] || "Economy";
}
